/// <amd-module name="Agile/Common/Utils/QueuedRequestManager/QueuedRequestManager" />
import {
    EntityID,
    IEntityReference,
    IRequestExecutorData,
    IRequestHandle,
    IRequestMetadata,
    RequestCompletionBehavior,
    RequestState
} from "Agile/Common/Utils/QueuedRequestManager/QueuedRequestManager.types";
import { RequestNode, IPredecessorData } from "Agile/Common/Utils/QueuedRequestManager/RequestNode";
import { QueueClearedDueToRejectionError, stringifyEntityReference } from "Agile/Common/Utils/QueuedRequestManager/Utils";

/**
 * A queue-like structure which supports tracking predecessors between requests.
 * Each request "targets" a particular "entity" (by its type and id), and can
 * specify other entities which it also needs to wait for. The request will
 * be executed when all predecessors are resolved. Requests can signal that
 * they have caused a change to their target entity's id; the updated value
 * will be passed to successor requests so they can reference it in their
 * request.
 */
export class QueuedRequestManager {
    /**
     * References to the most-recently-queued request node for each entity that
     * we know of. This is the tail we add new requests to. Key is the
     * stringified entity key.
     */
    private _lastRequestsByEntity: {
        [stringifiedKey: string]: RequestNode<any>;
    } = {};
    private _lastRequestsByEntityType: {
        [entityType: string]: RequestNode<any>;
    } = {};

    /**
     * Create a ref object that can be updated by the queue when a request returns an updated id
     * @param entityType The type of the entity
     * @param entityId The id of the entity
     */
    public createRef<TEntityId extends EntityID>(entityType: string, entityId: TEntityId): IEntityReference<TEntityId> {
        return {
            entityId: entityId,
            entityType: entityType
        };
    }

    /**
     * Returns a promise which will resolve when the "slot" arrives for the
     * given request configuration (i.e., when all predecessors have completed).
     * The resolved value will be a handle object which the caller must use to
     * invoke either a "success" or "failure" callback when their work for this
     * request completes. Callbacks must be awaited before continuing to ensure
     * the queue can finalize ID updates.
     *
     * The returned promise may be rejected with a QueueClearedDueToRejectionError
     * if a preceeding request failed.
     * @param requestMetadata the configuration for this request
     */
    public waitForPredecessors<TResult>(requestMetadata: IRequestMetadata<TResult>): Promise<IRequestHandle<TResult>> {
        return new Promise<IRequestHandle<TResult>>((resolveBegin, rejectBegin) => {
            let requestPromise: Promise<any>;

            // implementation of an "executeRequest" delegate which will resolve the outer promise and pass it the
            // callbacks to communicate the result of our caller's operation back to awaiters of executeRequest.
            const executeRequest = () =>
                new Promise<TResult>((resolveExecutor, rejectExecutor) =>
                    resolveBegin({
                        registerFinished: async value => {
                            resolveExecutor(value);
                            return requestPromise;
                        },
                        registerFailed: async () => {
                            rejectExecutor();
                            return requestPromise;
                        }
                    })
                );

            requestPromise = this.queueRequest(requestMetadata, {
                executeRequest: executeRequest
            }).catch(error => {
                // A QueueClearedDueToRejectionError means that the operation was cancelled before executeRequest
                // was called; in this case we need to reject the original waitForPredecessors promise because our
                // normal logic to settle it on execution doesn't run.
                if (error instanceof QueueClearedDueToRejectionError) {
                    rejectBegin(error);
                }
            });
        });
    }

    /**
     * Queues a request which will execute the passed delegate and returns a
     * promise representing that request's resolution/rejection.
     *
     * @param requestMetadata the configuration for this request
     * @param executor the configuration for the job that this request will execute
     */
    public queueRequest<TResult>(requestMetadata: IRequestMetadata<TResult>, executor: IRequestExecutorData<TResult>): Promise<TResult> {
        /*tslint:disable:promise-must-complete*/
        return new Promise<TResult>((resolve, reject) => {
            const newNode = new RequestNode<TResult>(requestMetadata, executor, resolve, reject);

            const predecessors = requestMetadata.predecessors || [];
            // Build a collection of predecessors we'll put into our new node
            const predecessorNodes: IPredecessorData[] = predecessors
                .map(predecessor => ({
                    node: this._lastRequestsByEntity[stringifyEntityReference(predecessor)],
                    entityRefToUpdate: predecessor
                }))
                // If any of those dependencies targeted entities we didn't know about (meaning we can consider it
                // already resolved), filter it out.
                .filter(predecessorData => !!predecessorData.node);

            // If we have existing requests for this entity queued, our new request needs to depend on it so it waits
            if (newNode.entityKey in this._lastRequestsByEntity) {
                // This request was added for internal tracking, so we don't have a ref object which needs to
                // be updated (ref objects are passed in by the user and then we modify them by reference to
                // update their ID)
                predecessorNodes.push({
                    entityRefToUpdate: null,
                    node: this._lastRequestsByEntity[newNode.entityKey]
                });
            }

            // If we have existing requests for this entity queued, our new request needs to depend on it so it waits
            for (const predecessorType of newNode.predecessorTypes) {
                if (predecessorType in this._lastRequestsByEntityType) {
                    // This request was added for internal tracking, so we don't have a ref object which needs to
                    // be updated (ref objects are pa ssed in by the user and then we modify them by reference to
                    // update their ID)
                    predecessorNodes.push({
                        entityRefToUpdate: null,
                        node: this._lastRequestsByEntityType[predecessorType]
                    });
                }
            }

            // Now that we've built the collection of predecessors, store it in the node.
            newNode.predecessorNodes = predecessorNodes;

            // Add this new node as a successor to all nodes we just said it depends on
            for (const predecessor of newNode.predecessorNodes) {
                // Add this node as a successor
                predecessor.node.successorNodes.push(newNode);
            }

            // We've added this request to the chain, so we can move the reference to the end of the chain to point to it
            this._lastRequestsByEntity[newNode.entityKey] = newNode;
            this._lastRequestsByEntityType[newNode.entityType] = newNode;

            // Start the new request if its dependencies are all satisfied already
            this._startRequestIfPossible(newNode);
        });
        /*tslint:enable:promise-must-complete*/
    }

    /**
     * Returns "true" if this queue has any requests which are either pending or
     * executing. False otherwise.
     */
    public isActive(): boolean {
        return Object.keys(this._lastRequestsByEntity).length !== 0;
    }

    /**
     * Returns "true" if this queue has any requests which are either pending or
     * executing targeting an entity of this type. False otherwise.
     */
    public areEntityTypesActive(...entityTypes: string[]): boolean {
        return Object.keys(this._lastRequestsByEntity).some(key => entityTypes.indexOf(this._lastRequestsByEntity[key].entityType) >= 0);
    }

    /**
     * Starts the given request if all its dependencies are satisfied.
     * @param request The request to try to start
     */
    private _startRequestIfPossible(request: RequestNode<any>): void {
        // If we have a node for a request, it's either pending or executing
        const allPredecessorsSatisfied = request.predecessorNodes.length === 0;

        if (allPredecessorsSatisfied) {
            this._startRequest(request);
        }
    }

    /**
     * Starts the given request (no check for dependencies)
     * @param request The request to start
     */
    private _startRequest(request: RequestNode<any>): void {
        if (!request.isPending) {
            throw new Error("This request is already complete or executing; it can't be started again.");
        }

        request.state = RequestState.Executing;
        request.executeRequest().then(
            value => {
                request.state = RequestState.Settled;
                // Update all the IDs
                this._evaluateIdUpdate(request, value);
                // Call back to the calling function
                request.invokeResolve(value);
                // Queue the next request
                this._executeCompletionBehavior(request.successBehavior, request);
            },
            reason => {
                request.state = RequestState.Settled;
                if (!(reason instanceof QueueClearedDueToRejectionError)) {
                    this._onRequestRejected(request);
                }

                request.invokeReject(reason);
            }
        );
    }

    /**
     * Swaps the entity ID on the given node, and does the same for all its
     * successors which target the same node.
     * @param node The node to begin on
     * @param newId The new ID of the entity this node targets
     */
    private _propagateIdUpdateForTargetEntity(node: RequestNode<any>, newId: EntityID) {
        for (const successorNode of node.successorNodes.filter(successor => successor.entityKey === node.entityKey)) {
            this._propagateIdUpdateForTargetEntity(successorNode, newId);
        }
        node.swapEntityId(newId);
    }

    /**
     * Evaluates the given request's ID update behavior, and handles the ID change.
     * @param request The request to operate on
     * @param requestPayload The payload which this request resolved with
     */
    private _evaluateIdUpdate<TResult>(request: RequestNode<TResult>, requestPayload: TResult) {
        const finalEntityId = request.evaluateGetUpdatedEntityId(requestPayload);
        const oldId = request.entityId;
        const oldKey = request.entityKey;

        if (finalEntityId !== oldId) {
            this._propagateIdUpdateForTargetEntity(request, finalEntityId);
            this._swapLastRequestsByEntityKey(oldKey, request.entityKey);
        }
    }

    /**
     * Cancels the given request by rejecting it with a QueueClearedDueToRejectionError
     * @param request The request to cancel
     */
    private _cancelRequest(request: RequestNode<any>): void {
        request.state = RequestState.Settled;
        request.invokeReject(new QueueClearedDueToRejectionError());
    }

    /**
     * Updates the entry corresponding to the given key in
     * _lastRequestsByEntity with a new one.
     * @param oldKey The existing key
     * @param newKey The new key
     */
    private _swapLastRequestsByEntityKey(oldKey: string, newKey: string): void {
        const value = this._lastRequestsByEntity[oldKey];
        delete this._lastRequestsByEntity[oldKey];
        this._lastRequestsByEntity[newKey] = value;
    }

    /**
     * Handles the removal of a request from the queue and starts any requests
     * which were waiting for it to complete.
     * @param request The request to remove from the queue
     */
    private _popAndExecuteSuccessors(request: RequestNode<any>) {
        let onlyRequestOfEntity = true;
        for (const successorNode of request.successorNodes) {
            // Remember if we've seen any other requests which depend on this request's entity
            if (successorNode.entityKey === request.entityKey) {
                onlyRequestOfEntity = false;
            }

            request.removePredecessor(successorNode);
            this._startRequestIfPossible(successorNode);
        }

        // If there aren't any more requests queued for this entity, clean up references to it
        if (onlyRequestOfEntity) {
            delete this._lastRequestsByEntity[request.entityKey];
        }

        if (this._lastRequestsByEntityType[request.entityType] === request) {
            delete this._lastRequestsByEntityType[request.entityType];
        }
    }

    /**
     * Handles the removal of a request from the queue and cancels any successors.
     * @param request The request to remove
     */
    private _popAndFlushSuccessors(request: RequestNode<any>) {
        for (const successorNode of request.successorNodes) {
            this._popAndFlushSuccessors(successorNode);
            request.removePredecessor(successorNode);
            this._cancelRequest(successorNode);
        }
        delete this._lastRequestsByEntity[request.entityKey];

        if (this._lastRequestsByEntityType[request.entityType] === request) {
            delete this._lastRequestsByEntityType[request.entityType];
        }
    }

    /**
     * Executes the specified behavior on a request.
     */
    private _executeCompletionBehavior(behavior: RequestCompletionBehavior, request: RequestNode<any>) {
        switch (behavior) {
            case RequestCompletionBehavior.CancelQueued:
                this._popAndFlushSuccessors(request);
                break;
            case RequestCompletionBehavior.Continue:
                this._popAndExecuteSuccessors(request);
                break;
            default:
                throw new Error(`The specified completion behavior "${behavior}" is invalid.`);
        }
    }

    /**
     * Handles the rejection of a request
     */
    private _onRequestRejected(request: RequestNode<any>) {
        this._executeCompletionBehavior(request.failureBehavior, request);
    }
}
